'use client';

import { useState } from 'react';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { formatWorkingPeriods } from './_utils/format-working-periods.util';

import { Button } from '@/components/ui/button/button.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { ScheduleType, SearchPartnerData } from '@/app/(main)/(pages)/busca/[slug]/_types/search-partner-data.type';
import { getSchedulesSlotById } from '@/app/(main)/(pages)/_components/schedules/_services/get-schedules-slot-by-id.service';
import {
  buildSlotCartItems,
  getOfferedProcedures,
} from '@/app/(main)/(pages)/busca/[slug]/_components/search-schedules-list/search-card/_components/tabs/_utils/slot-cart-items.util';
import { isOnlineConsultationSearch } from '@/app/(main)/(pages)/busca/[slug]/_utils/is-online-consultation-search.util';
import { usePatientSelection } from '@/app/(main)/_components/patient-selection/_hooks/use-patient-selection.hook';
import { ADD_TO_CART_ERROR_MESSAGE, useAddToCart } from '@/hooks/use-add-to-cart.hook';
import { useCartAdditionConfirmation } from '@/hooks/use-cart-addition-confirmation.hook';
import { useToast } from '@/components/ui/use-toast';
import { getLogger } from '@/logger/logger';
import { PatientOption } from '@/types/patient-selection.types';
import { SlotStatus } from '@/app/(main)/(pages)/_components/schedules/_types/schedule-slot.type';
import { SearchApiScheduleType } from '@/app/(main)/(pages)/_types/search-api-response.type';

const AVAILABLE_DAYS_TO_READ = 1;

export const FLEX_UNAVAILABLE_MESSAGE
  = 'Este Encaixe não está disponível no momento. Tente novamente mais tarde.';

const logger = getLogger('search.card-clinic-tab-flex');

interface SearchCardClinicTabFlexProps {
  schedule: SearchPartnerData;
}

export function SearchCardClinicTabFlex({ schedule }: SearchCardClinicTabFlexProps) {
  const [isFindingSlot, setIsFindingSlot] = useState(false);
  const flexSchedule = schedule.schedulesTypes[ScheduleType.FLEX]?.[0];
  const { requestPatientSelection } = usePatientSelection();
  const { addToCart, isAdding } = useAddToCart();
  const { confirmCartAddition } = useCartAdditionConfirmation();
  const { toast } = useToast();
  const waitingTime = flexSchedule?.waitingTime;
  const formattedPeriods = formatWorkingPeriods(flexSchedule?.workingPeriods ?? []);

  const hasInfo = Boolean(waitingTime || formattedPeriods);

  const findFlexSlotId = async (scheduleId: string): Promise<string | undefined> => {
    try {
      const { slots } = await getSchedulesSlotById({
        id: scheduleId,
        limitDays: AVAILABLE_DAYS_TO_READ,
        offsetDays: 0,
      });

      const slotId = slots.find(
        (slot) => slot.type === SearchApiScheduleType.FLEX && slot.status === SlotStatus.AVAILABLE,
      )?.id;

      if (!slotId) {
        logger.info('No open slot on the flex schedule', {
          cart: {
            event: 'no_open_slot',
            stage: 'slot_lookup',
            target: 'search_api',
            partner_id: schedule.partner.id,
            partner_facility_id: schedule.partner.facility.id,
            schedule_id: scheduleId,
          },
        });
      }

      return slotId;
    } catch (error) {
      logger.error('Failed to read the flex schedule slot', {
        cart: {
          event: 'slot_read_failed',
          stage: 'slot_lookup',
          target: 'search_api',
          partner_id: schedule.partner.id,
          partner_facility_id: schedule.partner.facility.id,
          schedule_id: scheduleId,
        },
        error,
      });

      return undefined;
    }
  };

  const handleSelectPatient = async (slotId: string, patient: PatientOption) => {
    const unpricedProcedures = getOfferedProcedures(schedule).filter((procedure) => !procedure.price);

    if (unpricedProcedures.length > 0) {
      logger.error('Blocked a purchase without a procedure price', {
        cart: {
          event: 'unpriced_procedure',
          stage: 'price_guard',
          target: 'cart',
          partner_id: schedule.partner.id,
          partner_facility_id: schedule.partner.facility.id,
          procedure_ids: unpricedProcedures.map((procedure) => procedure.id),
          slot_id: slotId,
        },
      });
      toast.error(ADD_TO_CART_ERROR_MESSAGE);

      return;
    }

    await addToCart(buildSlotCartItems(schedule, patient.id, slotId));
  };

  const handleContinue = () => {
    if (!flexSchedule?.id) {
      logger.error('Flex schedule has no id', {
        cart: {
          event: 'missing_schedule_id',
          stage: 'slot_lookup',
          target: 'search_api',
          partner_id: schedule.partner.id,
          partner_facility_id: schedule.partner.facility.id,
        },
      });
      toast.error(FLEX_UNAVAILABLE_MESSAGE);

      return;
    }

    let slotId = '';

    const prepareSlot = async (): Promise<boolean> => {
      setIsFindingSlot(true);

      slotId = await findFlexSlotId(flexSchedule.id).finally(() => setIsFindingSlot(false)) ?? '';

      if (!slotId) {
        toast.error(FLEX_UNAVAILABLE_MESSAGE);

        return false;
      }

      return confirmCartAddition(
        {
          partnerId: schedule.partner.id,
          facility: schedule.partner.facility,
          isOnline: isOnlineConsultationSearch(schedule),
        },
        ScheduleType.FLEX,
        { isStandalone: false },
      );
    };

    requestPatientSelection({
      ageRange: { minAge: flexSchedule.minAge, maxAge: flexSchedule.maxAge },
      onBeforeSelection: prepareSlot,
      onSelectPatient: (patient) => handleSelectPatient(slotId, patient),
    });
  };

  return (
    <div className="flex flex-col gap-2" data-testid={DataTestIdEnum.SEARCH_CARD_CLINIC_TAB_FLEX}>
      <div>
        <Typography
          variant={TypographyVariant.MEDIUM_BODY_TEXT}
          data-testid={DataTestIdEnum.SEARCH_CARD_CLINIC_FLEX_DESCRIPTION}
        >
          Após a compra, agende seu horário diretamente com a clínica ou profissional, conforme disponibilidade.
        </Typography>

        {hasInfo && (
          <div className="mt-5 flex flex-col">
            {waitingTime && (
              <Typography
                variant={TypographyVariant.MEDIUM_BODY_TEXT}
                data-testid={DataTestIdEnum.SEARCH_CARD_CLINIC_FLEX_WAITING_TIME}
              >
                <Typography as="span" variant={TypographyVariant.MEDIUM_BODY_TEXT} className="font-bold">
                  Tempo de encaixe:
                </Typography>
                {' '}
                {waitingTime}
                {' '}
                {Number(waitingTime) === 1 ? 'dia' : 'dias'}
              </Typography>
            )}
            {formattedPeriods && (
              <Typography
                variant={TypographyVariant.MEDIUM_BODY_TEXT}
                data-testid={DataTestIdEnum.SEARCH_CARD_CLINIC_FLEX_WORKING_HOURS}
              >
                <Typography as="span" variant={TypographyVariant.MEDIUM_BODY_TEXT} className="font-bold">
                  Horário de funcionamento:
                </Typography>
                {' '}
                {formattedPeriods}
              </Typography>
            )}
          </div>
        )}
      </div>

      <Button
        className="py-2"
        data-testid={DataTestIdEnum.SEARCH_CARD_CLINIC_FLEX_CONTINUE_BUTTON}
        isLoading={isFindingSlot || isAdding}
        onClick={handleContinue}
      >
        Continuar compra
      </Button>
    </div>
  );
}
